import type { FormEvent } from 'react'
import { useState } from 'react'
import { Link, Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom'
import {
  Building2,
  CheckCircle2,
  Pencil,
  ShieldOff,
  User,
  Package,
  FileText,
  AlertTriangle,
  Loader2,
  Mail,
  MessageSquare,
  MapPin,
  Phone,
  Globe2,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { consignmentLabel } from '@/src/components/QuickEdit'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { FactCard, StatCard } from '@/components/ui/stat'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { CustomerActions } from '@/app/(dashboard)/customers/[id]/_components/CustomerActions'
import { CustomerTabs } from '@/app/(dashboard)/customers/[id]/_components/CustomerTabs'
import { CustomerDetails } from '@/app/(dashboard)/customers/[id]/_components/CustomerDetails'
import { CustomerEditDialog } from '@/app/(dashboard)/customers/[id]/_components/CustomerEditDialog'
import { AuditLogTable } from '@/app/(dashboard)/customers/[id]/_components/AuditLogTable'
import { apiClient } from '@/lib/api/client'
import { customerIdentityValue } from '@/lib/customer-identity'
import type {
  Consignment,
  Customer,
  CustomerSignInEmailStatus,
  HelmCommunication,
  HelmOutboundCommunicationAttachment,
  Profile,
} from '@/lib/api/types'
import { CommunicationAttachmentPicker } from '@/src/components/CommunicationAttachmentPicker'
import { HelmCcSelector } from '@/src/components/HelmCcSelector'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { LoadingState } from '@/src/components/LoadingState'
import { ErrorState } from '@/src/components/ErrorState'
import { TranslatableMessage } from '@/src/components/translation/Translatable'
import { customerGreeting, greetableName } from '@/lib/customer-messages'

type CustomerContext = {
  customer: Customer
  profile: Profile | null
  consignments: Consignment[]
  communications: HelmCommunication[]
  /** Admins only: the customer's confirmed login and any pending sign-in email change. */
  signIn: CustomerSignInEmailStatus | null
  reload: () => void
}

function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not set'
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function useCustomerContext() {
  return useOutletContext<CustomerContext>()
}

function normalizeWhatsAppPhone(phone: string | null | undefined) {
  return phone?.replace(/\D/g, '') || ''
}

function communicationCcRecipients(communication: HelmCommunication) {
  const cc = communication.metadata?.cc
  return Array.isArray(cc)
    ? cc.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : []
}

function CustomerCommunicationsCard({
  customer,
  communications,
  isAdmin,
  onChanged,
}: {
  customer: Customer
  communications: HelmCommunication[]
  isAdmin: boolean
  onChanged: () => void
}) {
  // Named in words, never by an email address that stands in for a name.
  const subjectName = greetableName(customer.company_name) ?? greetableName(customer.name)
  const defaultSubject = subjectName ? `Helm update for ${subjectName}` : 'Helm update'
  const defaultBody = `${customerGreeting(customer)}\n\nWe have an update on your Helm inquiry.\n\nRegards,\nXtellar Helm`
  const whatsappPhone = normalizeWhatsAppPhone(customer.phone)
  const [emailSubject, setEmailSubject] = useState(defaultSubject)
  const [emailBody, setEmailBody] = useState(defaultBody)
  const [ccRecipients, setCcRecipients] = useState<string[]>([])
  const [attachments, setAttachments] = useState<HelmOutboundCommunicationAttachment[]>([])
  const [uploadingAttachments, setUploadingAttachments] = useState(false)
  const [whatsappBody, setWhatsappBody] = useState(defaultBody)
  const [submitting, setSubmitting] = useState<'EMAIL' | 'WHATSAPP' | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleQueueEmail(event: FormEvent) {
    event.preventDefault()
    setSubmitting('EMAIL')
    setError(null)
    try {
      await apiClient.createCommunication({
        customer_id: customer.id,
        channel: 'EMAIL',
        template_key: 'HELM_CUSTOMER_UPDATE',
        recipient: customer.email,
        cc: ccRecipients,
        subject: emailSubject.trim() || defaultSubject,
        body: emailBody.trim(),
        attachments,
        send: true,
      })
      setAttachments([])
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to send customer email')
    } finally {
      setSubmitting(null)
    }
  }

  async function handleOpenWhatsApp() {
    if (!whatsappPhone) {
      setError('Customer phone is not set')
      return
    }

    setSubmitting('WHATSAPP')
    setError(null)
    const body = whatsappBody.trim() || defaultBody
    try {
      await apiClient.createCommunication({
        customer_id: customer.id,
        channel: 'WHATSAPP',
        status: 'LOGGED',
        template_key: 'HELM_CUSTOMER_WHATSAPP_UPDATE',
        recipient: `+${whatsappPhone}`,
        body,
      })
      window.open(
        `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(body)}`,
        '_blank',
        'noopener,noreferrer',
      )
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to log customer WhatsApp contact')
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Communications</CardTitle>
        <CardDescription>Customer-level email queue and WhatsApp contact history.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isAdmin ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <form onSubmit={handleQueueEmail} className="space-y-3 rounded-md border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">Email</div>
                  <div className="text-sm text-muted-foreground">{customer.email}</div>
                </div>
                <Button
                  type="submit"
                  size="sm"
                  disabled={submitting !== null || uploadingAttachments || !customer.email}
                >
                  {submitting === 'EMAIL' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  Send
                </Button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-email-subject">Subject</Label>
                <Input
                  id="customer-email-subject"
                  value={emailSubject}
                  onChange={(event) => setEmailSubject(event.target.value)}
                  disabled={submitting !== null}
                />
              </div>
              <HelmCcSelector
                value={ccRecipients}
                onChange={setCcRecipients}
                disabled={submitting !== null}
                excludeEmails={[customer.email]}
              />
              <div className="space-y-2">
                <Label htmlFor="customer-email-body">Message</Label>
                <Textarea
                  id="customer-email-body"
                  value={emailBody}
                  onChange={(event) => setEmailBody(event.target.value)}
                  disabled={submitting !== null}
                  rows={5}
                />
              </div>
              <CommunicationAttachmentPicker
                value={attachments}
                onChange={setAttachments}
                onError={setError}
                onUploadingChange={setUploadingAttachments}
                disabled={submitting !== null}
              />
            </form>

            <div className="space-y-3 rounded-md border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">WhatsApp</div>
                  <div className="text-sm text-muted-foreground">
                    {customer.phone || 'No phone on customer'}
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleOpenWhatsApp}
                  disabled={submitting !== null || !whatsappPhone}
                >
                  {submitting === 'WHATSAPP' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MessageSquare className="h-4 w-4" />
                  )}
                  Open
                </Button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-whatsapp-body">Message</Label>
                <Textarea
                  id="customer-whatsapp-body"
                  value={whatsappBody}
                  onChange={(event) => setWhatsappBody(event.target.value)}
                  disabled={submitting !== null}
                  rows={7}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-md border p-4 text-sm text-muted-foreground">
            Admin access is required to queue or log customer communications.
          </div>
        )}

        <div className="divide-y rounded-md border">
          {communications.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              No customer-level communications have been recorded.
            </p>
          ) : (
            communications.slice(0, 10).map((communication) => {
              const cc = communicationCcRecipients(communication)
              return (
                <div key={communication.id} className="space-y-2 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={communication.channel === 'WHATSAPP' ? 'secondary' : 'outline'}>
                      {formatLabel(communication.channel)}
                    </Badge>
                    <Badge variant="outline">{formatLabel(communication.status)}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {new Date(communication.sent_at || communication.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">{communication.recipient}</div>
                  {cc.length > 0 && (
                    <div className="text-xs text-muted-foreground">CC: {cc.join(', ')}</div>
                  )}
                  <TranslatableMessage subject={communication.subject} body={communication.body} />
                </div>
              )
            })
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function CustomerDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, error, loading, reload } = useAsyncData(async () => {
    if (!id) throw new Error('Customer id is missing')

    const [customer, profile, consignments, communications] = await Promise.all([
      apiClient.getCustomer(id),
      apiClient.getProfile().catch(() => null),
      apiClient.listConsignments().catch(() => []),
      apiClient.listCommunications({ customer_id: id, limit: 50 }).catch(() => []),
    ])

    const admin = Boolean(profile?.is_admin || profile?.role === 'admin')
    const signIn = admin ? await apiClient.getCustomerSignInEmail(id).catch(() => null) : null

    return {
      customer,
      profile,
      consignments: consignments.filter((consignment) => consignment.customer_id === id),
      communications,
      signIn,
    }
  }, [id])
  // A fresh key per opening, so the dialog starts from the customer as last loaded.
  const [editorKey, setEditorKey] = useState(0)
  const [editing, setEditing] = useState(false)
  const [saved, setSaved] = useState(false)
  // A sign-in email change started from the dialog shows on the page once it closes.
  const [signInChanged, setSignInChanged] = useState(false)

  if (loading) return <LoadingState label="Loading customer..." />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data?.customer || !id) return <ErrorState error="Customer not found" />

  const { customer, profile, consignments, communications, signIn } = data
  const isAdmin = profile?.is_admin || profile?.role === 'admin'
  const isArchived = !!customer.deleted_at
  const displayName = customer.company_name || customer.name
  const invoiceCount = consignments.filter(
    (consignment) => consignment.invoice_url || consignment.original_invoice_url,
  ).length

  return (
    <div className="space-y-6">
      <PageHeader
        title={displayName}
        description={[
          customer.customer_number,
          customer.type === 'BUSINESS' ? customer.name : customerIdentityValue(customer, 'email'),
        ]
          .filter(Boolean)
          .join(' · ')}
        breadcrumbs={[{ label: 'Customers', href: '/customers' }, { label: displayName }]}
        actions={
          isAdmin ? (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => {
                  setSaved(false)
                  setEditorKey((key) => key + 1)
                  setEditing(true)
                }}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <CustomerActions
                customer={customer}
                isAdmin={isAdmin}
                onChanged={reload}
                onDeleted={() => navigate('/customers')}
              />
            </>
          ) : undefined
        }
      />

      {isAdmin && (
        <CustomerEditDialog
          key={editorKey}
          customer={customer}
          open={editing}
          onOpenChange={(open) => {
            setEditing(open)
            if (!open && signInChanged) {
              setSignInChanged(false)
              reload()
            }
          }}
          onSaved={() => {
            setEditing(false)
            setSaved(true)
            setSignInChanged(false)
            reload()
          }}
          signIn={signIn}
          onSignInChange={() => setSignInChanged(true)}
        />
      )}

      <div className="space-y-4">
        {saved && (
          <Alert role="status">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>Customer details saved.</AlertDescription>
          </Alert>
        )}

        {isArchived && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This customer is archived. They are hidden from the default customer list.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={customer.type === 'BUSINESS' ? 'default' : 'secondary'}>
            {customer.type === 'BUSINESS' ? (
              <>
                <Building2 className="mr-1 h-3 w-3" /> Business
              </>
            ) : (
              <>
                <User className="mr-1 h-3 w-3" /> Individual
              </>
            )}
          </Badge>
          {isArchived && <Badge variant="destructive">Archived</Badge>}
          {customer.portal_status === 'SUSPENDED' && (
            <Badge variant="destructive" title="Locked out of Helm and Tarifix">
              <ShieldOff className="mr-1 h-3 w-3" /> Suspended
            </Badge>
          )}
          {customer.is_placeholder && <Badge variant="outline">Provisional identity</Badge>}
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
          <StatCard label="Consignments" icon={Package} value={consignments.length} />
          <StatCard label="Invoices" icon={FileText} value={invoiceCount} />
          <FactCard
            className="col-span-2 md:col-span-1"
            label="TIN Number"
            value={customerIdentityValue(customer, 'tin_number') || 'Not confirmed'}
            valueClassName="font-mono"
          />
        </div>

        <CustomerTabs
          customerId={id}
          consignmentsCount={consignments.length}
          invoicesCount={invoiceCount}
        />

        <Outlet
          context={
            {
              customer,
              profile,
              consignments,
              communications,
              signIn,
              reload,
            } satisfies CustomerContext
          }
        />
      </div>
    </div>
  )
}

export function CustomerOverviewPage() {
  const { customer, profile, communications, signIn, reload } = useCustomerContext()
  const isAdmin = Boolean(profile?.is_admin || profile?.role === 'admin')
  const contacts = customer.contacts ?? []
  const addresses = customer.addresses ?? []

  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.75fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Customer Snapshot</CardTitle>
            <CardDescription>
              Commercial profile, portal readiness, and customer identifiers.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Customer Number</Label>
              <p className="mt-1 font-medium">{customer.customer_number || 'Not assigned'}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Portal Status</Label>
              <p className="mt-1 font-medium">{formatLabel(customer.portal_status)}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Default Currency</Label>
              <p className="mt-1 font-medium">{customer.default_currency || 'USD'}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Language</Label>
              <p className="mt-1 font-medium">{customer.customer_language || 'English'}</p>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase text-muted-foreground">Website</Label>
              <p className="mt-1 break-all font-medium">
                {customer.website_url ? (
                  <a
                    href={customer.website_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <Globe2 className="h-4 w-4" />
                    {customer.website_url}
                  </a>
                ) : (
                  'Not set'
                )}
              </p>
            </div>
            {customer.notes && (
              <div className="md:col-span-2">
                <Label className="text-xs uppercase text-muted-foreground">Notes</Label>
                <p className="mt-1 whitespace-pre-wrap text-sm">{customer.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Primary Contact</CardTitle>
            <CardDescription>The person Helm should contact first.</CardDescription>
          </CardHeader>
          <CardContent>
            {contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No contact persons recorded.</p>
            ) : (
              contacts
                .filter((contact) => contact.is_primary)
                .concat(contacts.filter((contact) => !contact.is_primary))
                .slice(0, 1)
                .map((contact) => (
                  <div key={contact.id || contact.name} className="space-y-3">
                    <div>
                      <div className="text-lg font-semibold">{contact.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {[contact.title, contact.department].filter(Boolean).join(' · ') ||
                          'No role set'}
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      {contact.primary_email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          {contact.primary_email}
                        </div>
                      )}
                      {contact.primary_phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          {contact.primary_phone}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">{formatLabel(contact.preferred_channel)}</Badge>
                        {contact.portal_access && <Badge variant="secondary">Portal access</Badge>}
                      </div>
                    </div>
                  </div>
                ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Contact Persons</CardTitle>
            <CardDescription>
              {contacts.length} contact{contacts.length === 1 ? '' : 's'} available for operations,
              finance, and logistics updates.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No contact persons recorded.</p>
            ) : (
              <div className="divide-y rounded-md border">
                {contacts.map((contact) => (
                  <div key={contact.id || contact.name} className="space-y-2 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-medium">{contact.name}</div>
                      {contact.is_primary && <Badge variant="secondary">Primary</Badge>}
                      <Badge variant="outline">{formatLabel(contact.preferred_channel)}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {[contact.title, contact.department].filter(Boolean).join(' · ') ||
                        'No role set'}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      {contact.emails.map((email) => (
                        <span key={email} className="inline-flex items-center gap-1">
                          <Mail className="h-3.5 w-3.5" />
                          {email}
                        </span>
                      ))}
                      {contact.phones.map((phone) => (
                        <span key={phone} className="inline-flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5" />
                          {phone}
                        </span>
                      ))}
                    </div>
                    {contact.notes && (
                      <p className="text-sm text-muted-foreground">{contact.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Addresses</CardTitle>
            <CardDescription>
              {addresses.length} address{addresses.length === 1 ? '' : 'es'} captured for billing,
              shipping, consignee, and delivery workflows.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {addresses.length === 0 ? (
              <p className="text-sm text-muted-foreground">No customer addresses recorded.</p>
            ) : (
              <div className="divide-y rounded-md border">
                {addresses.map((address) => (
                  <div key={address.id || address.label} className="space-y-2 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-medium">{address.label}</div>
                      <Badge variant="outline">{formatLabel(address.type)}</Badge>
                      {address.is_default_billing && <Badge variant="secondary">Billing</Badge>}
                      {address.is_default_shipping && <Badge variant="secondary">Shipping</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {[address.recipient_name, address.company_name].filter(Boolean).join(' · ') ||
                        'No recipient set'}
                    </div>
                    <div className="flex gap-2 text-sm">
                      <MapPin className="mt-0.5 h-4 w-4 flex-none text-muted-foreground" />
                      <p className="whitespace-pre-wrap">
                        {address.raw_address ||
                          [
                            address.address_line1,
                            address.address_line2,
                            address.city,
                            address.region,
                            address.country,
                          ]
                            .filter(Boolean)
                            .join(', ') ||
                          'Address details pending'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      {address.email && (
                        <span className="inline-flex items-center gap-1">
                          <Mail className="h-3.5 w-3.5" />
                          {address.email}
                        </span>
                      )}
                      {address.phone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5" />
                          {address.phone}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle id="customer-details">Customer Details</CardTitle>
        </CardHeader>
        <CardContent>
          <CustomerDetails customer={customer} signIn={signIn} />
        </CardContent>
      </Card>
      <CustomerCommunicationsCard
        key={customer.id}
        customer={customer}
        communications={communications}
        isAdmin={isAdmin}
        onChanged={reload}
      />
    </div>
  )
}

export function CustomerConsignmentsPage() {
  const { consignments } = useCustomerContext()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Consignments</CardTitle>
      </CardHeader>
      <CardContent>
        {consignments.length === 0 ? (
          <p className="text-muted-foreground">No consignments recorded for this customer.</p>
        ) : (
          <div className="divide-y rounded-md border">
            {consignments.map((consignment) => (
              <div
                key={consignment.id}
                className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 space-y-2">
                  <div className="font-medium">{consignmentLabel(consignment)}</div>
                  {consignment.tracking_number && (
                    <div className="text-xs text-muted-foreground">
                      Tracking {consignment.tracking_number}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{formatLabel(consignment.service_type)}</Badge>
                    <Badge variant="outline">{formatLabel(consignment.inquiry_stage)}</Badge>
                    <Badge variant="outline">
                      {formatLabel(consignment.preferred_communication_channel)}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                    <span>{consignment.destination_address || consignment.destination_port}</span>
                    <span>{formatLabel(consignment.status)}</span>
                    <span>{new Date(consignment.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                    <span>Owner: {consignment.owner_user_id || 'Unassigned'}</span>
                    <span>Next: {consignment.next_action || 'Not set'}</span>
                  </div>
                </div>
                <Button size="sm" variant="outline" asChild>
                  <Link to={`/consignments/${consignment.id}`}>Open</Link>
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function CustomerInvoicesPage() {
  const { consignments } = useCustomerContext()
  const invoices = consignments.filter(
    (consignment) => consignment.invoice_url || consignment.original_invoice_url,
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoices</CardTitle>
      </CardHeader>
      <CardContent>
        {invoices.length === 0 ? (
          <p className="text-muted-foreground">No invoice documents found for this customer.</p>
        ) : (
          <div className="divide-y rounded-md border">
            {invoices.map((consignment) => (
              <div
                key={consignment.id}
                className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <div className="font-medium">{consignmentLabel(consignment)}</div>
                  <div className="text-sm text-muted-foreground">
                    {new Date(consignment.created_at).toLocaleDateString()}
                  </div>
                </div>
                <Button size="sm" variant="outline" asChild>
                  <a
                    href={consignment.invoice_url || consignment.original_invoice_url || '#'}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open Invoice
                  </a>
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function CustomerActivityPage() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity Log</CardTitle>
      </CardHeader>
      <CardContent>
        <AuditLogTable logs={[]} />
      </CardContent>
    </Card>
  )
}
